import { SMWorkflowOrchestrationServiceLambda } from "@amzn/sm-workflow-orchestration-service-js-client";
import { orchestratorServiceAPIConfig } from "src/utils/api-gateway/orchestrator-service-api-mappings";
import { getStage } from "src/utils/env";
import {
    CapabilityEntries,
    CapabilityEntry,
    WorkflowDetails
} from "@amzn/sm-workflow-orchestration-service-js-client/lib/smworkfloworchestrationservicelambda";
import { useQuery } from "@tanstack/react-query";
import {CONTINUOUS_PROGRAM_RECIPE} from "src/constants/workflow-instance";
import {
    HIDDEN_WORKFLOW_DEFINITIONS, HIDDEN_CLASSIFICATION_TAGS
} from "src/constants/start-workflow-hidden-dropdown-values";

export class CapabilityService {
    private workflowOrchestrationServiceLambda: SMWorkflowOrchestrationServiceLambda;

    constructor() {
        const orchestratorServiceConfig = orchestratorServiceAPIConfig(getStage());
        this.workflowOrchestrationServiceLambda = new SMWorkflowOrchestrationServiceLambda({
            endpoint: `https://${orchestratorServiceConfig.endpoint}/${orchestratorServiceConfig.stage}`,
            region: orchestratorServiceConfig.region
        });
    }

    async getAllCapabilities(): Promise<CapabilityEntry[]> {
        const response = await this.workflowOrchestrationServiceLambda
            .getAllCapabilityDetails()
            .promise();
        return response.capabilityEntries || [];
    }

    static useCapabilities = () => {
        return useQuery(
            ['capabilities'],
            async () => {
                const capabilityService = new CapabilityService();
                const Data = await capabilityService.getAllCapabilities();
                /**
                 * TEMPORARY FILTERING LOGIC
                 * 
                 * This client-side filtering removes specific workflows and classification tags from the UI
                 * to address immediate business requirements. This is a temporary workaround until the
                 * backend service can be updated to handle filtering.
                 * 
                 * TODO: Remove this filtering logic once backend filtering is implemented
                 * Tracking: https://issues.amazon.com/issues/SMT-1420661
                 */
                const filteredData = Data.map(entry => ({
                    ...entry,
                    workflows: entry.workflows
                        .filter(workflow => !HIDDEN_WORKFLOW_DEFINITIONS.includes(workflow.recipeName || ''))
                        .map(workflow => ({
                            ...workflow,
                            allowedTags: {
                                ...workflow.allowedTags,
                                classificationTag: (workflow.allowedTags?.classificationTag || [])
                                    .filter(tag => !HIDDEN_CLASSIFICATION_TAGS.includes(tag)),
                                subClassificationTag: workflow.allowedTags?.subClassificationTag || []
                            }
                        }))
                }));
                return filteredData;
            },
            {
                staleTime: Infinity,
                cacheTime: Infinity
            }
        );
    };

    static getClassificationTagsWithRecipes(capabilitiesData: CapabilityEntries) {
        const programRecipeMap = new Map<string, string>();
        const programSubProgramMap = new Map<string, string[]>();
        const continuousRecipeMap = new Map<string, string>();

        capabilitiesData.forEach((entry: CapabilityEntry) => {
            entry.workflows.forEach((workflow: WorkflowDetails) => {
                const classificationTags = workflow.allowedTags?.classificationTag || [];
                const subClassificationTags = workflow.allowedTags?.subClassificationTag || [];

                classificationTags.forEach((tag: string) => {
                    if (!programRecipeMap.has(tag)) {
                        programRecipeMap.set(tag, workflow.recipeName as string);
                    }
                    if (workflow.recipeName === CONTINUOUS_PROGRAM_RECIPE) {
                        continuousRecipeMap.set(tag, workflow.recipeName as string);
                    }
                    const subClassificationTagForClassificationTag = workflow.tagRelationshipMap?.[tag] || [];
                    const validSubClassificationTags = subClassificationTags.filter(subTag =>
                        subClassificationTagForClassificationTag.includes(subTag)
                    );
                    programSubProgramMap.set(tag, validSubClassificationTags);
                });
            });
        });
        const getRecipeForProgram = (program: string, isContinuous?: boolean): string => {
            if (!program) return '';
            if (isContinuous && continuousRecipeMap.has(program)) {
                return continuousRecipeMap.get(program) || '';
            }
            return programRecipeMap.get(program) || '';
        };
        return {
            programRecipeMap,
            programSubProgramMap,
            getRecipeForProgram
        };
    }
}
